import { Button } from 'antd';
import { I18nt } from 'utils';
import { LangText, ModalContent } from 'utils/interfaces';
import {
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	LoadingOutlined,
} from '@ant-design/icons';
import styles from './ConsultaMedicamentos.module.less';
import { useConsultaMedicamentos } from '../ConsultaMedicamentosContext';
import InfoModal from 'components/InfoModal';
import ModalWithImage from 'components/ModalwithImage';
import cn from 'classnames';
import { useState } from 'react';

export function FormResponseModal() {
	const {
		statusMessage,
		resetState,
		getBotiquines,
		dispatch,
		reservaExistente,
	} = useConsultaMedicamentos();

	const modalContent: ModalContent = {
		'-1': {
			title: 'numero_de_afiliado_no_encontrado',
			body: 'el_numero_que_ingresaste',
		},
		'-3': {
			title: 'el_numero_de_receta_no',
			body: 'porfavor_dirigete_a_tu_medico',
		},
		'-4': {
			title: 'receta_no_asociada',
			body: 'el_numero_de_receta_que',
		},
		'-2': {
			title: 'numero_afiliado_inactivo',
			body: 'para_poder_consultar',
		},
		'-5': {
			title: 'receta_despachada',
			body: 'la_receta_ya',
		},
		'-6': {
			title: 'receta_no_vigente',
			body: 'el_numero_de_receta',
		},
		'-7': {
			title: 'medicamento_especial',
			body: 'debe_acercarse_al_establecimiento',
		},
		'rm-5': {
			title: 'la_reserva_expiro',
			body: 'superate_el_numero_de_intentos',
		},
		'rm-6': {
			title: 'la_reserva_expiro',
			body: 'superate_el_numero_de_intentos',
		},
		'rm-7': {
			title: 'medicamento_no_retirado',
			body: 'el_medicamento_no_fue_retirado_tienes',
			buttonText: 'continuar',
			action: getBotiquines,
			dynamicText: {
				intentos: `${reservaExistente?.reaperturasRestantes ?? 0}`,
			},
		},
		rectificarReceta: {
			title: 'porfavor_dirigirse_a_su_medico',
		},
		duiNotFound: {
			title: 'dui_no_encontrado',
			body: 'el_numero_que_ingresaste',
		},
		vence_ahora: {
			title: 'tu_receta_vence_hoy',
			body: 'si_haces_una_reserva',
			action: () => {
				dispatch({ type: 'setStatusMessage' });
			},
		},
	};

	if (!statusMessage) {
		return null;
	}
	const message = modalContent[statusMessage];
	if (!message) {
		return null;
	}
	return (
		<InfoModal
			onOk={typeof message.action === 'function' ? message.action : resetState}
			okText={message.buttonText ?? 'de_acuerdo'}
			body={message.body}
			title={message.title}
			dynamicText={message.dynamicText}
			visible={Boolean(statusMessage)}
		/>
	);
}

export function InfoRecetaModal() {
	const { infoReceta, showInfoReceta, getBotiquines, setStatusMessage } =
		useConsultaMedicamentos();

	if (!infoReceta) {
		return null;
	}

	return (
		<ModalWithImage
			centered
			footer={null}
			closable={false}
			visible={showInfoReceta}
			className={styles.infoRecetaModal}
		>
			<header>
				<CheckCircleOutlined />
				<h4>{I18nt('informacion_de_receta')}</h4>
			</header>
			<div className={styles.infoRecetaModalBody}>
				<ul>
					<li>
						<strong>{I18nt('paciente')}:&nbsp;</strong>
						{`${infoReceta.nombrePaciente} ${infoReceta.apellidoPaciente}`}
					</li>
					<li>
						<strong>{I18nt('medicamento')}:&nbsp;</strong>
						{infoReceta.medicamento}
					</li>
					<li>
						<strong>{I18nt('presentacion')}:&nbsp;</strong>
						{infoReceta.presentacion}
					</li>
					<li>
						<strong>{I18nt('cantidad')}:&nbsp;</strong>
						{infoReceta.cantidad}
					</li>
				</ul>
			</div>
			<footer>
				<p>{I18nt('es_este_tu_medicamento')}</p>
				<div>
					<Button onClick={() => setStatusMessage('rectificarReceta')}>
						{I18nt('no')}
					</Button>
					<Button onClick={getBotiquines} type='primary'>
						{I18nt('si')}
					</Button>
				</div>
			</footer>
		</ModalWithImage>
	);
}

export function MiRecetaModal() {
	const { infoReceta, showMiReceta, setShowMiReceta } =
		useConsultaMedicamentos();

	if (!infoReceta) {
		return null;
	}

	return (
		<ModalWithImage
			centered
			width={309}
			maskClosable
			footer={null}
			closable={false}
			visible={showMiReceta}
			className={styles.infoRecetaModal}
			onCancel={setShowMiReceta}
		>
			<header>
				<h4>{I18nt('mi_receta')}</h4>
			</header>
			<div className={styles.infoRecetaModalBody}>
				<ul>
					<li>
						<strong>{I18nt('paciente')}:&nbsp;</strong>
						{`${infoReceta.nombrePaciente} ${infoReceta.apellidoPaciente}`}
					</li>
					<li>
						<strong>{I18nt('medicamento')}:&nbsp;</strong>
						{infoReceta.medicamento}
					</li>
					<li>
						<strong>{I18nt('presentacion')}:&nbsp;</strong>
						{infoReceta.presentacion}
					</li>
					<li>
						<strong>{I18nt('cantidad')}:&nbsp;</strong>
						{infoReceta.cantidad}
					</li>
				</ul>
			</div>
		</ModalWithImage>
	);
}

export function ReservarMedicamentoModal() {
	const { botiquin, infoReceta, reservarMedicamento, dispatch } =
		useConsultaMedicamentos();
	const [isLoading, setIsLoading] = useState(false);

	if (!botiquin || !infoReceta) {
		return null;
	}

	const reservarMedicamentoHandler = () => {
		reservarMedicamento(setIsLoading);
	};

	return (
		<ModalWithImage
			centered
			footer={null}
			closable={false}
			visible={Boolean(botiquin)}
			className={cn(styles.infoRecetaModal, styles.reservaMedicamento)}
		>
			<header>
				<ExclamationCircleOutlined />
				<h4>{I18nt('reservar_medicamento')}</h4>
			</header>
			<div className={styles.text}>
				Tu reserva <strong>expirará</strong> dentro de
				<strong> 24 horas,</strong> retira tu medicamento antes que acabe el
				tiempo o tu reserva será <strong>anulada.</strong>
			</div>
			<div className={styles.infoRecetaModalBody}>
				<ul>
					<li>
						<strong>{I18nt('lugar')}:&nbsp;</strong>
						{botiquin.botiquin}
					</li>
					<li>
						<strong>{I18nt('medicamento')}:&nbsp;</strong>
						{infoReceta.medicamento}
					</li>
					<li>
						<strong>{I18nt('presentacion')}:&nbsp;</strong>
						{infoReceta.presentacion}
					</li>
					<li>
						<strong>{I18nt('cantidad')}:&nbsp;</strong>
						{infoReceta.cantidad}
					</li>
				</ul>
			</div>
			<footer>
				<p>{I18nt('deseas_reservar_este_medicamento')}</p>
				<div>
					<Button
						onClick={() => dispatch({ type: 'setBotiquinForReservation' })}
					>
						{I18nt('no')}
					</Button>
					<Button onClick={reservarMedicamentoHandler} type='primary'>
						{I18nt('si')}
						{isLoading && <LoadingOutlined />}
					</Button>
				</div>
			</footer>
		</ModalWithImage>
	);
}

export function ReservaExistenteModal() {
	const {
		dispatch,
		getBotiquines,
		statusMessage,
		reservaExistente: reserva,
		showModalReservaExistente: showModal,
	} = useConsultaMedicamentos();

	if (!showModal || !reserva || statusMessage) {
		return null;
	}

	const onConfirmation = () => {
		getBotiquines();
		dispatch({ type: 'setModalReservaExistente', payload: false });
	};

	const footer = () => {
		if (
			reserva.modificacionesRestantes &&
			reserva.modificacionesRestantes > 0
		) {
			return (
				<footer>
					<p>{I18nt('quieres_modificar_la_reserva')}</p>
					{reserva.modificacionesRestantes === 1 && (
						<p className={styles.ultimaModificacionText}>
							{I18nt('esta_es_tu_ultima_modificacion')}
						</p>
					)}

					<div>
						<Button onClick={() => dispatch({ type: 'reset' })}>
							{I18nt('no')}
						</Button>
						<Button onClick={onConfirmation} type='primary'>
							{I18nt('si')}
						</Button>
					</div>
				</footer>
			);
		}
		return (
			<footer>
				{I18nt('no_puedes_hacer_mas_modificaciones')}
				<div>
					<Button onClick={() => dispatch({ type: 'reset' })} type='primary'>
						{I18nt('de_acuerdo')}
					</Button>
				</div>
			</footer>
		);
	};

	return (
		<ModalWithImage
			centered
			footer={null}
			closable={false}
			className={styles.infoRecetaModal}
			visible={showModal}
		>
			<header>
				<CheckCircleOutlined />
				<h4>{I18nt('ya_tienes_una_reserva')}</h4>
			</header>
			<div className={cn(styles.text, styles.reservaEsistenteText)}>
				{I18nt('tu_reserva_expira')}
				&nbsp;
				<strong>{reserva.hora}</strong>
			</div>
			<div className={styles.infoRecetaModalBody}>
				<ul>
					<li>
						<strong>{I18nt('medicamento')}:&nbsp;</strong>
						{reserva.medicamento}
					</li>
					<li>
						<strong>{I18nt('presentacion')}:&nbsp;</strong>
						{reserva.presentacion}
					</li>
					<li>
						<strong>{I18nt('cantidad')}:&nbsp;</strong>
						{reserva.cantidad}
					</li>
					<li>
						<strong>{I18nt('lugar')}:&nbsp;</strong>
						{reserva.lugar}
					</li>
				</ul>
			</div>
			{footer()}
		</ModalWithImage>
	);
}
